import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useState } from "react";
import { FaEye } from "react-icons/fa";
import { Link } from "react-router-dom";
import { api_Routes } from "../../../tools/api_Routes";
import { Helper } from "../../../tools/Helper";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

const DataTable = ({
                     data,
                     getData,
                     selected,
                     selectAllMode,
                     onSelectionChange,
                     totalCount,
                     page,
                     rowsPerPage,
                     sortConfig,
                     onSort,
                   }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState({
    id: null,
    status: null,
  });
  const [loadingId, setLoadingId] = useState(null);

  const handleStatusChange = async (id, newStatus) => {
    try {
      setLoadingId(id);
      const { response, message } = await Helper.Post({
        url: `${api_Routes.UserAds.UpdateStatus}/${id}`,
        hasToken: true,
        data: { status: newStatus },
      });

      if (!response) {
        throw new Error(message || "Failed to update status");
      }

      await getData();
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoadingId(null);
      setShowConfirmDialog(false);
    }
  };

  const handleStatusChangeInit = (id, newStatus) => {
    setPendingChange({ id, status: newStatus });
    setShowConfirmDialog(true);
  };

  const handleSelect = (event, id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange(newSelected, selectAllMode);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Enter select-all mode
      onSelectionChange(new Set(), true);
    } else {
      // Exit select-all mode
      onSelectionChange(new Set(), false);
    }
  };

  const isSelected = (id) => {
    if (selectAllMode) {
      return !selected.has(id); // In select-all mode, selected Set contains EXCLUDED items
    }
    return selected.has(id);
  };

  const isHeaderCheckboxIndeterminate = () => {
    return !selectAllMode && selected.size > 0 && selected.size < data.length;
  };

  const getSortDirection = (field) => {
    if (sortConfig.field === field) {
      return sortConfig.direction;
    }
    return undefined;
  };

  const getColumnDisplayName = (column) => {
    switch (column) {
      case "ad_sub_category_type_id":
        return "Category";
      case "user_id":
        return "User";
      case "created_at":
        return "Free Ad Date";
      default:
        return column.charAt(0).toUpperCase() + column.slice(1);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Ads Table", 14, 10);
    doc.autoTable({
      startY: 20,
      head: [
        ["ID", "Title", "Address", "Price", "Status", "Category", "User ID", "Free Ad Date"],
      ],
      body: data.map((item) => [
        item.id,
        item.title,
        item.address,
        item.price,
        item.status,
        item.ad_sub_category_type_id,
        item.user_id,
        item.created_at || 'N/A',
      ]),
    });
    doc.save("ads_table.pdf");
  };

  const statusOptions = [
    "in_progress",
    "active",
    "complete",
    "pending",
    "cancel",
  ];

  const columns = [
    "id",
    "title",
    "address",
    "price",
    "status",
    "category",
    "user_id",
    "created_at"
  ];

  return (
      <>
        {/*<button*/}
        {/*    onClick={exportPDF}*/}
        {/*    className="bg-primary text-white p-2 rounded mb-3"*/}
        {/*>*/}
        {/*  Export to PDF*/}
        {/*</button>*/}
        <Dialog
            open={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
        >
          <DialogTitle>Confirm Status Change</DialogTitle>
          <DialogContent>
            {pendingChange.id && (
                <p>
                  Are you sure you want to change status for Ad #{pendingChange.id}{" "}
                  to <strong>{pendingChange.status.replace("_", " ")}</strong>?
                </p>
            )}
          </DialogContent>
          <DialogActions>
            <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
                onClick={() =>
                    handleStatusChange(pendingChange.id, pendingChange.status)
                }
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
            >
              Confirm
            </button>
          </DialogActions>
        </Dialog>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow className="bg-primary">
                <TableCell padding="checkbox">
                  <Checkbox
                      color="primary"
                      indeterminate={isHeaderCheckboxIndeterminate()}
                      checked={selectAllMode || selected.size > 0}
                      onChange={handleSelectAll}
                  />
                </TableCell>
                {columns.map((column) => (
                    <TableCell
                        key={column}
                        align="center"
                        className="!text-darkTextColor whitespace-nowrap"
                    >
                      <TableSortLabel
                          active={sortConfig.field === column}
                          direction={getSortDirection(column)}
                          onClick={() => onSort(column)}
                      >
                        {getColumnDisplayName(column)}
                      </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell
                    align="center"
                    className="!text-darkTextColor whitespace-nowrap"
                >
                  Photo
                </TableCell>
                <TableCell
                    align="center"
                    className="!text-darkTextColor whitespace-nowrap"
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => {
                const isItemSelected = isSelected(item.id);
                return (
                    <TableRow
                        key={item.id}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        hover
                        selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={(event) => handleSelect(event, item.id)}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row" align="center">
                        {item?.id}
                      </TableCell>
                      <TableCell align="center">{item?.title}</TableCell>
                      <TableCell align="center">{item?.address}</TableCell>
                      <TableCell align="center">
                        {item?.is_show_price ? `$${item?.price}` : "Price Hidden"}
                      </TableCell>
                      <TableCell align="center">
                        <select
                            value={item?.status}
                            onChange={(e) =>
                                handleStatusChangeInit(item.id, e.target.value)
                            }
                            className="border rounded p-1"
                            disabled={loadingId === item.id}
                        >
                          {statusOptions.map((option) => (
                              <option key={option} value={option}>
                                {option.replace("_", " ")}
                                {loadingId === item.id && option === item.status
                                    ? " (Updating...)"
                                    : ""}
                              </option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell align="center">
                        {item?.ad_sub_category_type_id}
                      </TableCell>
                      <TableCell align="center">{item?.user_id}</TableCell>
                      <TableCell align="center">
                        {item?.created_at
                            ? new Date(item.created_at).toLocaleDateString()
                            : 'N/A'
                        }
                      </TableCell>
                      <TableCell align="center">
                        {item?.photos?.length > 0 && (
                            <img
                                src={item.photos[0].photo}
                                alt="Ad"
                                className="w-10 h-10 object-cover rounded"
                            />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Link
                            to={`/UserAds/Ads/${item?.id}`}
                            color="inherit"
                            underline="none"
                        >
                          <FaEye className="text-blue-500" />
                        </Link>
                      </TableCell>
                    </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
  );
};

export default DataTable;